import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { SITE, THERAPIST_IMAGE } from "@/data/site";

const CREDS = ["Licensed Professional Counselor (LPC)", "CT License #003511", "15+ years of clinical experience", "Bilingual — English & Spanish", "Crisis intervention trained", "Art therapy informed"];
const FOCUS = ["Anxiety & panic", "Depression & low mood", "Couples & family", "Burnout & career", "Grief & life transitions"];

export default function Therapist() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Waleska Castillo",
    jobTitle: "Licensed Professional Counselor",
    worksFor: { "@type": "MedicalBusiness", name: SITE.name },
    address: { "@type": "PostalAddress", streetAddress: SITE.street, addressLocality: SITE.city, addressRegion: SITE.state, postalCode: SITE.zip },
    knowsLanguage: ["English", "Spanish"],
    image: THERAPIST_IMAGE,
    description: "Licensed Professional Counselor (CT License #003511) with 15+ years of experience serving Waterbury, CT and telehealth clients across Connecticut.",
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "license",
      recognizedBy: { "@type": "GovernmentOrganization", name: "Connecticut Department of Public Health" },
      identifier: "003511",
    },
  };
  return (
    <>
      <Seo
        title="Waleska Castillo, LPC — Therapist in Waterbury, CT | Let Go Wellness"
        description="Meet Waleska Castillo, LPC — a bilingual, compassionate therapist in Waterbury, CT. Credentials, experience and approach."
        path="/therapists/waleska-castillo"
        image={THERAPIST_IMAGE}
        jsonLd={schema}
      />
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:py-24">
          <Reveal delay={0.1}>
            <div className="aspect-[4/5] overflow-hidden"><img src={THERAPIST_IMAGE} alt="Waleska Castillo, LPC, therapist in Waterbury, CT" className="h-full w-full object-cover object-top" loading="eager" /></div>
          </Reveal>
          <Reveal>
            <div className="overline mb-5">Our therapist</div>
            <h1 className="font-serif text-5xl leading-tight text-ink sm:text-6xl">Waleska Castillo</h1>
            <p className="mt-3 font-sans text-sm uppercase tracking-[0.18em] text-clay">LPC · CT License #003511 · 15 Years of Experience</p>
            <p className="mt-6 font-sans text-base leading-relaxed text-ink-soft">
              Waleska is a Licensed Professional Counselor with more than 15 years of experience walking alongside people through anxiety, stress, grief and the quiet work of coming home to themselves. Fully bilingual in English and Spanish, she brings warmth, cultural sensitivity and a grounded, whole-person approach few clinicians can offer.
            </p>
            <p className="mt-4 font-sans text-base leading-relaxed text-ink-soft">
              Waleska blends the modern science of healing — CBT, ACT, IFS and mindfulness — with genuine presence, meeting the whole person, not just the symptom.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white py-20">
        <div className="mx-auto grid max-w-5xl gap-14 px-6 md:grid-cols-2">
          <div>
            <div className="overline mb-6">Credentials & training</div>
            <ul className="space-y-3">{CREDS.map((c) => <li key={c} className="flex items-start gap-3 font-sans text-base text-ink-soft"><Check size={17} className="mt-1 shrink-0 text-clay" />{c}</li>)}</ul>
            <p className="mt-6 font-sans text-xs leading-relaxed text-muted-foreground">Licensed by the Connecticut Department of Public Health · LPC License #003511 · 15+ years in practice.</p>
          </div>
          <div>
            <div className="overline mb-6">Areas of focus</div>
            <ul className="space-y-3">{FOCUS.map((c) => <li key={c} className="flex items-start gap-3 font-sans text-base text-ink-soft"><Check size={17} className="mt-1 shrink-0 text-clay" />{c}</li>)}</ul>
            <Link to="/new-patient" className="mt-8 inline-flex rounded-full bg-ink px-8 py-3.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85" data-testid="therapist-cta">Request Waleska as your therapist</Link>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
